import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert as RNAlert, View, Text, ScrollView, ActivityIndicator, Dimensions, Modal, Platform, TextInput, TouchableOpacity, TouchableWithoutFeedback, Image } from 'react-native';
import { ToastContainer, toast } from 'react-toastify';
import styles from '../../styles/AppStyles';
import { COLORS } from '../constants/colors';

// Calculate a fixed pane height based on window size
// const windowHeight = Dimensions.get('window').height;

const windowHeight = Dimensions.get('window').height;
// Adjust this offset to approximate left pane content height (header, paddings)
//const PREVIEW_OFFSET = 260 -40;
const PREVIEW_OFFSET = 10; // approx 1200px header height
const previewHeight = windowHeight - PREVIEW_OFFSET;
const EMPTY_STRING_ARRAY: string[] = [];


type PreviewPaneProps = {
  loading: boolean;
  generatedHtml: string | null;
  regenerableImageUrls?: string[];
  regeneratedImageUrls?: string[];
  hasAcceptedRegenerations?: boolean;
  onRegenerateImage?: (originalImageUrl: string, prompt: string, referenceImageFiles: File[]) => Promise<string>;
  onAcceptRegeneratedImage?: (originalImageUrl: string, newImageUrl: string) => void;
  onDownloadUpdatedTemplate?: () => void;
  progressStatus?: string;
  previewDevice?: 'desktop' | 'mobile';
};

export default function PreviewPane({
  loading,
  generatedHtml,
  regenerableImageUrls = EMPTY_STRING_ARRAY,
  regeneratedImageUrls = EMPTY_STRING_ARRAY,
  hasAcceptedRegenerations = false,
  onRegenerateImage,
  onAcceptRegeneratedImage,
  onDownloadUpdatedTemplate,
  progressStatus,
  previewDevice = 'desktop'
}: PreviewPaneProps) {
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null);
  const [generatedCandidateSrc, setGeneratedCandidateSrc] = useState<string | null>(null);
  const [imagePrompt, setImagePrompt] = useState('');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [referenceImagesLabel, setReferenceImagesLabel] = useState('No images selected');
  const [referenceImageFiles, setReferenceImageFiles] = useState<File[]>([]);
  const [hasCompletedGeneration, setHasCompletedGeneration] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const wasLoadingRef = useRef(loading);

  const showMessage = useCallback((title: string, message: string) => {
    if (Platform.OS === 'web') {
      toast.error(message, {
        containerId: 'preview-modal-toast',
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
      });
      return;
    }
    RNAlert.alert(title, message);
  }, []);

  const closeImageModal = useCallback(() => {
    setSelectedImageSrc(null);
    setGeneratedCandidateSrc(null);
    setImagePrompt('');
    setReferenceImageFiles([]);
    setReferenceImagesLabel('No images selected');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleSelectReferenceImages = useCallback(() => {
    if (Platform.OS !== 'web') return;
    fileInputRef.current?.click();
  }, []);

  const handleReferenceImagesChange = useCallback((event: any) => {
    const files: FileList | null | undefined = event?.target?.files;
    const count = files?.length ?? 0;
    setReferenceImageFiles(files ? Array.from(files) : []);
    setReferenceImagesLabel(count > 0 ? `${count} image${count === 1 ? '' : 's'} selected` : 'No images selected');
  }, []);

  const handleRemoveReferenceImages = useCallback(() => {
    setReferenceImageFiles([]);
    setReferenceImagesLabel('No images selected');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleGenerateImage = useCallback(async () => {
    if (!selectedImageSrc) return;
    if (!onRegenerateImage) return;

    if (!imagePrompt.trim()) {
      showMessage('Missing Prompt', 'Please enter an image prompt before generating.');
      return;
    }

    setIsGeneratingImage(true);
    try {
      const newImageUrl = await onRegenerateImage(selectedImageSrc, imagePrompt, referenceImageFiles);
      setGeneratedCandidateSrc(newImageUrl);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to regenerate image.';
      showMessage('Regeneration Error', message);
    } finally {
      setIsGeneratingImage(false);
    }
  }, [imagePrompt, onRegenerateImage, referenceImageFiles, selectedImageSrc, showMessage]);

  const handleKeepOriginal = useCallback(() => {
    closeImageModal();
  }, [closeImageModal]);

  const handleAcceptNewImage = useCallback(() => {
    if (!selectedImageSrc || !generatedCandidateSrc) return;
    onAcceptRegeneratedImage?.(selectedImageSrc, generatedCandidateSrc);
    closeImageModal();
  }, [closeImageModal, generatedCandidateSrc, onAcceptRegeneratedImage, selectedImageSrc]);

  const normalizeImageUrl = useCallback((value: string) => {
    const candidate = value.trim();
    if (!candidate) return '';

    try {
      const parsed = new URL(candidate, window.location.href);
      return parsed.href;
    } catch {
      return candidate;
    }
  }, []);

  const isRegenerableImage = useCallback((src: string) => {
    if (regenerableImageUrls.length === 0) return false;

    const normalizedSrc = normalizeImageUrl(src);
    if (!normalizedSrc) return false;

    const normalizedSrcWithoutQuery = normalizedSrc.split('?')[0];

    return regenerableImageUrls.some((candidate) => {
      const normalizedCandidate = normalizeImageUrl(candidate);
      if (!normalizedCandidate) return false;

      return (
        normalizedCandidate === normalizedSrc ||
        normalizedCandidate.split('?')[0] === normalizedSrcWithoutQuery
      );
    });
  }, [normalizeImageUrl, regenerableImageUrls]);

  const isAlreadyRegeneratedImage = useCallback((src: string) => {
    if (regeneratedImageUrls.length === 0) return false;

    const normalizedSrc = normalizeImageUrl(src);
    if (!normalizedSrc) return false;
    const normalizedSrcWithoutQuery = normalizedSrc.split('?')[0];

    return regeneratedImageUrls.some((candidate) => {
      const normalizedCandidate = normalizeImageUrl(candidate);
      if (!normalizedCandidate) return false;

      return (
        normalizedCandidate === normalizedSrc ||
        normalizedCandidate.split('?')[0] === normalizedSrcWithoutQuery
      );
    });
  }, [normalizeImageUrl, regeneratedImageUrls]);

  const openRegenerateModalForImage = useCallback((src: string) => {
    if (isAlreadyRegeneratedImage(src)) {
      showMessage('Limit Reached', 'This image has already been regenerated. Only one regeneration is allowed per image.');
      return;
    }

    if (!isRegenerableImage(src)) return;

    setSelectedImageSrc(src);
    setGeneratedCandidateSrc(null);
  }, [isAlreadyRegeneratedImage, isRegenerableImage, showMessage]);

  useEffect(() => {
    if (wasLoadingRef.current && !loading && !!generatedHtml) {
      setHasCompletedGeneration(true);
    }

    wasLoadingRef.current = loading;
  }, [generatedHtml, loading]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    let cleanupFns: Array<() => void> = [];

    const clearButtons = () => {
      cleanupFns.forEach((cleanup) => cleanup());
      cleanupFns = [];
    };

    const applyButtons = () => {
      clearButtons();

      const container = previewContainerRef.current;
      if (!container || !generatedHtml || loading || !hasCompletedGeneration) return;

      const images = Array.from(container.querySelectorAll('img'));

      images.forEach((image) => {
        const initialSrc = image.currentSrc || image.src;
        if (!initialSrc) return;
        if (!isRegenerableImage(initialSrc)) return;
        if (isAlreadyRegeneratedImage(initialSrc)) return;

        const host = image.parentElement;
        if (!host) return;

        const computedHostStyle = window.getComputedStyle(host);
        const previousPosition = host.style.position;
        const previousDisplay = host.style.display;

        if (computedHostStyle.position === 'static') {
          host.style.position = 'relative';
        }

        if (computedHostStyle.display === 'inline') {
          host.style.display = 'inline-block';
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'preview-regenerate-button';
        button.textContent = 'Regenerate';
        button.style.position = 'absolute';
        button.style.top = '10px';
        button.style.right = '10px';
        button.style.zIndex = '20';
        button.style.padding = '8px 12px';
        button.style.border = 'none';
        button.style.borderRadius = '8px';
        button.style.cursor = 'pointer';
        button.style.fontWeight = '600';
        button.style.fontSize = '14px';
        button.style.background = 'rgba(0, 0, 0, 0.65)';
        button.style.color = '#fff';
        button.style.backdropFilter = 'blur(2px)';
        button.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)';

        const handleButtonClick = (event: Event) => {
          event.preventDefault();
          event.stopPropagation();

          const latestSrc = image.currentSrc || image.src;
          if (!latestSrc) return;

          openRegenerateModalForImage(latestSrc);
        };

        button.addEventListener('click', handleButtonClick);
        host.appendChild(button);

        cleanupFns.push(() => {
          button.removeEventListener('click', handleButtonClick);
          button.remove();

          if (computedHostStyle.position === 'static') {
            host.style.position = previousPosition;
          }

          if (computedHostStyle.display === 'inline') {
            host.style.display = previousDisplay;
          }
        });
      });
    };

    applyButtons();
    const delayedPass = window.setTimeout(applyButtons, 250);

    return () => {
      window.clearTimeout(delayedPass);
      clearButtons();
    };
  }, [generatedHtml, loading, hasCompletedGeneration, previewDevice, selectedImageSrc, generatedCandidateSrc, openRegenerateModalForImage]);

  const previewDivClassName = previewDevice === 'mobile'
    ? 'previewDiv previewDeviceMobile'
    : 'previewDiv';

  return (
    <View style={{height: 'auto', borderBottomEndRadius: 12, borderBottomStartRadius: 12, overflow: 'hidden', flex: 1, position: 'relative'}}>
      {Platform.OS === 'web' ? (
          <ToastContainer containerId="preview-modal-toast" />
      ) : null}
      {loading ? (
        <View
          style={[styles.loadingContainer, { height: previewHeight - 40 }]}
          accessible
          accessibilityLabel="Loading landing page"
        >
          <Text style={styles.loadingIcon}>✨</Text>
          <Text style={styles.loadingText}>{progressStatus || 'Loading...'}</Text>
          <ActivityIndicator size="large" style={styles.loadingIndicator} />
        </View>
      ) : (
        <ScrollView
          style={[styles.previewScrollView,{ height: previewHeight - 40 }]}
          contentContainerStyle={{ padding: 10 }}
          nestedScrollEnabled
        >
          {generatedHtml ? (
            <>
              <View style={{ backgroundColor: '#0a0a0a', borderWidth: 1, borderColor: COLORS.BRAND_PRIMARY, borderRadius: 8, padding: 10, marginBottom: 10, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={{ color: '#aaa', fontSize: 14, flex: 1 }}>
                  This is a <Text style={{ color: '#fff', fontWeight: '500' }}>preview</Text> of an Elementor template. The final result may differ from what is displayed here.
                </Text>
              </View>
              <div
                ref={previewContainerRef}
                className={previewDivClassName}
                dangerouslySetInnerHTML={{ __html: generatedHtml }}
              />
            </>
          ) : (
            <View style={{ backgroundColor: '#0a0a0a', borderWidth: 1, borderColor: COLORS.BRAND_PRIMARY, borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <Text style={{ color: '#aaa', fontSize: 14 }}>
                Preview will appear here after generation.
              </Text>
            </View>
          )}
        </ScrollView>
      )}

      {hasAcceptedRegenerations && onDownloadUpdatedTemplate && !loading ? (
        <View style={{ position: 'absolute', right: 16, bottom: 120, zIndex: 30 }}>
          <TouchableOpacity
            style={[styles.button, { flex: -1 }, styles.primaryButton]}
            onPress={onDownloadUpdatedTemplate}
            accessibilityRole="button"
            accessibilityLabel="Download updated template"
          >
            <Text style={styles.primaryButtonText}>Download Updated Template</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <Modal
        visible={!!selectedImageSrc && !generatedCandidateSrc}
        transparent={true}
        animationType="fade"
        onRequestClose={closeImageModal}
      >
        <TouchableWithoutFeedback onPress={closeImageModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <Text style={styles.sectionTitle}>Edit Image Prompt</Text>

                <Text style={[styles.boxLabelText, { marginBottom: 8 }]}>Image description:</Text>
                <TextInput
                  style={styles.textArea}
                  value={imagePrompt}
                  onChangeText={setImagePrompt}
                  placeholder="Enter a detailed description of the image you want to generate..."
                  placeholderTextColor="#fff"
                  multiline
                />

                  <Text style={[styles.boxLabelText, { marginTop: 12, marginBottom: 8 }]}>Optional: Use reference images (Subject + Product)</Text>
                <View style={[styles.uploadBox, { marginTop: 0 }]}> 

                  <Text style={{ color: '#fff', marginBottom: 12 }}>
                    You can upload one or more images (e.g. the product and a person). The AI will combine them based on your description.
                  </Text>

                  {Platform.OS === 'web' ? (
                    <input
                      ref={(el) => { fileInputRef.current = el; }}
                      type="file"
                      accept="image/*"
                      multiple
                      hidden
                      title="Reference images"
                      aria-label="Reference images"
                      onChange={handleReferenceImagesChange}
                    />
                  ) : null}

                  <View style={[styles.row, { alignItems: 'center', marginBottom: 0 }]}> 
                    <View style={[styles.input, { flex: 1, justifyContent: 'center', marginBottom: 0 }]}> 
                      <Text style={{ color: '#fff' }}>{referenceImagesLabel}</Text>
                    </View>

                    <TouchableOpacity
                      style={[styles.button, { flex: -1 }, styles.primaryButton]}
                      onPress={handleSelectReferenceImages}
                      accessibilityRole="button"
                      accessibilityLabel="Select reference images"
                    >
                      <Text style={styles.primaryButtonText}>Select Images</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, { flex: -1 }, styles.secondaryButton]}
                      onPress={handleRemoveReferenceImages}
                      accessibilityRole="button"
                      accessibilityLabel="Remove reference images"
                    >
                      <Text style={styles.secondaryButtonText}>Remove</Text>
                    </TouchableOpacity>
                  </View>
                </View>

                <View style={[styles.buttonRow, { justifyContent: 'flex-end', marginTop: 20, marginBottom: 0 }]}>
                  <TouchableOpacity
                    style={[styles.button, { flex: -1 }, styles.secondaryButton]}
                    onPress={closeImageModal}
                    accessibilityRole="button"
                    accessibilityLabel="Cancel"
                  >
                    <Text style={styles.secondaryButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.button, { flex: -1 }, styles.primaryButton]}
                    onPress={handleGenerateImage}
                    disabled={isGeneratingImage}
                    accessibilityRole="button"
                    accessibilityLabel="Generate image"
                  >
                    <Text style={styles.primaryButtonText}>{isGeneratingImage ? 'Generating...' : 'Generate Image'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      <Modal
        visible={!!selectedImageSrc && !!generatedCandidateSrc}
        transparent={true}
        animationType="fade"
        onRequestClose={closeImageModal}
      >
        <TouchableWithoutFeedback onPress={closeImageModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={[styles.modalContent, { width: 980, maxWidth: '95%' }]}>
                <Text style={styles.sectionTitle}>Confirm Image Replacement</Text>

                <View style={{ flexDirection: 'row', columnGap: 12, width: '100%' }}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.boxLabelText, { marginBottom: 8 }]}>Original Image</Text>
                    <View style={{ backgroundColor: '#1f1f1f', borderRadius: 8, padding: 8, minHeight: 320, alignItems: 'center', justifyContent: 'center' }}>
                      {selectedImageSrc ? (
                        <Image
                          source={{ uri: selectedImageSrc }}
                          accessibilityLabel="Original image"
                          style={{ width: '100%', height: 420, resizeMode: 'contain' }}
                        />
                      ) : null}
                    </View>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.boxLabelText, { marginBottom: 8 }]}>New Image</Text>
                    <View style={{ backgroundColor: '#1f1f1f', borderRadius: 8, padding: 8, minHeight: 320, alignItems: 'center', justifyContent: 'center' }}>
                      {generatedCandidateSrc ? (
                        <Image
                          source={{ uri: generatedCandidateSrc }}
                          accessibilityLabel="Regenerated image"
                          style={{ width: '100%', height: 420, resizeMode: 'contain' }}
                        />
                      ) : null}
                    </View>
                  </View>
                </View>

                <View style={[styles.buttonRow, { justifyContent: 'flex-end', marginTop: 20, marginBottom: 0 }]}>
                  <TouchableOpacity
                    style={[styles.button, { flex: -1 }, styles.secondaryButton]}
                    onPress={handleKeepOriginal}
                    accessibilityRole="button"
                    accessibilityLabel="Keep original image"
                  >
                    <Text style={styles.secondaryButtonText}>Keep Original</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.button, { flex: -1 }, styles.primaryButton]}
                    onPress={handleAcceptNewImage}
                    accessibilityRole="button"
                    accessibilityLabel="Use new image"
                  >
                    <Text style={styles.primaryButtonText}>Use New Image</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
}
